import React, { useEffect, useState } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import { IconX, IconExternalLink, IconAlertTriangle } from "@tabler/icons-react";
import { api } from "../../../api/Middleware";
import { CALENDAR_EVENTS_URL } from "../../../api/Urls";

/**
 * Modal que exibe o comprovante de pagamento de um agendamento.
 *
 * O endpoint `/api/v1/calendar_events/<id>/receipt/` exige Bearer JWT.
 * `<iframe src>` direto NÃO envia o token (vive em localStorage), então
 * fazemos fetch via `api` middleware (que injeta o header), recebemos
 * blob e geramos uma URL temporária via `URL.createObjectURL` que o
 * iframe pode renderizar localmente sem auth adicional.
 *
 * Suporta imagens (PNG/JPG/WebP) e PDFs nativamente.
 *
 * Props:
 *  - show: boolean
 *  - onHide: () => void
 *  - eventId: number — ID do CalendarEvents
 */
const ReceiptViewerModal = ({ show, onHide, eventId }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show || !eventId) {
      // Limpar estado quando modal fecha
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
        setBlobUrl(null);
      }
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get(`${CALENDAR_EVENTS_URL}${eventId}/receipt/`, { responseType: "blob" })
      .then((resp) => {
        if (cancelled) return;
        const url = URL.createObjectURL(resp.data);
        setBlobUrl(url);
      })
      .catch((e) => {
        if (cancelled) return;
        const msg =
          e?.response?.status === 404
            ? "Comprovante não encontrado."
            : e?.response?.data?.error || "Falha ao carregar o comprovante.";
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, eventId]);

  // Cleanup do blob URL quando o componente desmonta
  useEffect(() => {
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  if (!eventId) return null;

  const handleOpenInNewTab = () => {
    if (blobUrl) window.open(blobUrl, "_blank", "noopener,noreferrer");
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header style={{ padding: "12px 16px" }}>
        <Modal.Title style={{ fontSize: "1rem" }}>
          Comprovante de pagamento
        </Modal.Title>
        <div style={{ display: "flex", gap: 8 }}>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleOpenInNewTab}
            disabled={!blobUrl}
            title="Abrir em nova aba"
          >
            <IconExternalLink size={16} />
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={onHide}>
            <IconX size={16} />
          </Button>
        </div>
      </Modal.Header>
      <Modal.Body style={{ padding: 0, minHeight: "60vh" }}>
        {loading && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <Spinner animation="border" />
            <span className="text-muted">Carregando comprovante...</span>
          </div>
        )}
        {error && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "60vh",
              flexDirection: "column",
              gap: 12,
              color: "#dc3545",
            }}
          >
            <IconAlertTriangle size={48} />
            <span>{error}</span>
          </div>
        )}
        {!loading && !error && blobUrl && (
          <iframe
            src={blobUrl}
            title="Comprovante de pagamento"
            style={{
              width: "100%",
              height: "75vh",
              border: 0,
              display: "block",
            }}
          />
        )}
      </Modal.Body>
    </Modal>
  );
};

export default ReceiptViewerModal;
